/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useEffect, useState } from 'react';
import { Collapse, Spin, Alert, Button, Icon, Tag } from 'antd';
import moment from 'moment';
import { getAppConfigHistory } from '../../_shared/services/utils.service';
import { diffConfigs, formatValue } from './configHelpers';
import { styles } from './editconfig.styles';

const { Panel } = Collapse;

// Read-only audit trail of settings saves. Entries are recorded by the server
// on every save (who, when, full before/after), so each panel renders the
// exact per-key diff of that save. Loaded lazily the first time the tab is
// opened, and reloaded after this page saves (refreshKey changes).
export const HistoryPanel = ({ active, refreshKey }) => {
    const [history, setHistory] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = async () => {
        try {
            setLoading(true);
            setError(null);
            setHistory(await getAppConfigHistory());
        } catch (err) {
            setError(err.message || 'Failed to load settings history');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (active) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, refreshKey]);

    if (!active && history === null) return null;

    if (error) {
        return (
            <Alert
                type="error"
                showIcon
                message="Could not load settings history"
                description={error}
                style={{ marginBottom: 16 }}
            />
        );
    }

    if (loading && history === null) {
        return <Spin style={{ display: 'block', margin: '40px auto' }} />;
    }

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ color: '#888', fontSize: 12, flex: 1 }}>
                    Every save is recorded with who saved it and exactly what changed.
                </span>
                <Button size="small" onClick={load} loading={loading}>
                    <Icon type="reload" /> Refresh
                </Button>
            </div>

            {(history || []).length === 0 ? (
                <Alert
                    type="info"
                    showIcon
                    message="No history yet"
                    description="History starts recording with the next settings save."
                />
            ) : (
                <Collapse bordered={false}>
                    {history.map(entry => {
                        const changes = diffConfigs(entry.before, entry.after);
                        return (
                            <Panel
                                key={entry.id}
                                header={
                                    <span style={{ fontSize: 13 }}>
                                        <strong>{entry.lastSaved ? moment(entry.lastSaved).format('MMM D, YYYY h:mm A') : 'Unknown time'}</strong>
                                        <span style={{ color: '#888', margin: '0 8px' }}>{entry.savedBy || 'unknown user'}</span>
                                        <Tag>{changes.length} change{changes.length === 1 ? '' : 's'}</Tag>
                                    </span>
                                }
                            >
                                {changes.length === 0 ? (
                                    <div style={{ color: '#888', fontSize: 12 }}>Saved with no changes.</div>
                                ) : (
                                    <div css={styles.diffTable}>
                                        {changes.map(change => (
                                            <div className="diffRow" key={change.key}>
                                                <code className="diffKey">{change.key}</code>
                                                <div className="diffValues">
                                                    <span className="oldVal">{formatValue(change.oldValue)}</span>
                                                    <span className="arrow">→</span>
                                                    <span className="newVal">{formatValue(change.newValue)}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Panel>
                        );
                    })}
                </Collapse>
            )}
        </div>
    );
};
